export interface IdbStoreConfig {
  dbName:     string;
  dbVersion:  number;
  storeNames: readonly string[];
  storeName:  string;
  keyPath?:   string;
}
export interface IdbCacheEntry<T> {
  key:       string;
  data:      T;
  fetchedAt: number;
  ttlMs:     number;
}

export const QBA_CACHE_CONFIG = {
  dbName: 'qba_cache',
  dbVersion: 2,
  storeNames: ['employee_cache', 'override_employee_cache'],
} as const;

const dbCache = new Map<string, Promise<IDBDatabase>>();

function openDb(config: IdbStoreConfig): Promise<IDBDatabase> {
  const cacheKey = `${config.dbName}:${config.dbVersion}`;
  if (dbCache.has(cacheKey)) return dbCache.get(cacheKey)!;

  const promise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(config.dbName, config.dbVersion);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      const kp = config.keyPath ?? 'key';
      for (const name of config.storeNames) {
        if (!db.objectStoreNames.contains(name))
          db.createObjectStore(name, { keyPath: kp });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => {
      dbCache.delete(cacheKey);
      reject(req.error);
    };
  });

  dbCache.set(cacheKey, promise);
  return promise;
}

export function createIdbCache<T>(config: IdbStoreConfig) {
  const { storeName } = config;

  return {
    async get(key: string): Promise<T | null> {
      const db = await openDb(config);
      return new Promise((resolve, reject) => {
        const req = db.transaction(storeName, 'readonly').objectStore(storeName).get(key);
        req.onsuccess = () => {
          const entry: IdbCacheEntry<T> | undefined = req.result;
          if (!entry)                                      return resolve(null);
          if (Date.now() - entry.fetchedAt > entry.ttlMs) return resolve(null); // expired
          resolve(entry.data);
        };
        req.onerror = () => reject(req.error);
      });
    },

    async set(key: string, data: T, ttlMs: number): Promise<void> {
      const db = await openDb(config);
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).put({ key, data, fetchedAt: Date.now(), ttlMs } satisfies IdbCacheEntry<T>);
        tx.oncomplete = () => resolve();
        tx.onerror    = () => reject(tx.error);
      });
    },

    async delete(key: string): Promise<void> {
      const db = await openDb(config);
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror    = () => reject(tx.error);
      });
    },

    async clearAll(): Promise<void> {
      const db = await openDb(config);
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).clear();
        tx.oncomplete = () => resolve();
        tx.onerror    = () => reject(tx.error);
      });
    },

    async keys(): Promise<string[]> {
      const db = await openDb(config);
      return new Promise((resolve, reject) => {
        const req = db.transaction(storeName, 'readonly').objectStore(storeName).getAllKeys();
        req.onsuccess = () => resolve(req.result as string[]);
        req.onerror   = () => reject(req.error);
      });
    },
  };
}